import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ChefCoinIcon from '@/components/ui/ChefCoinIcon';
import { CountryDropdown } from '@/components/CountryDropdown';
import { COUNTRIES } from '@/types/user';
import { getCountryName } from '@/utils/countryUtils';
import type { DashboardUser } from '@/lib/dashboard-service';
import { AlertTriangle, Trash2 } from 'lucide-react';

type Role = 'admin' | 'reader' | 'guest';
type Subscription = 'free' | 'standard' | 'premium' | 'banned';

interface ManageUserDialogProps {
  user: DashboardUser | null;
  open: boolean;
  onClose: () => void;
  onSave: (fields: {
    name: string;
    country: string | null;
    role: Role;
    subscription: Subscription;
    chefcoins_balance: number;
  }) => void;
  onDelete: () => void;
  loading?: boolean;
}

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'reader', label: 'Reader' },
  { value: 'guest', label: 'Guest' },
];

const SUBSCRIPTION_OPTIONS: { value: Subscription; label: string }[] = [
  { value: 'free', label: 'Free' },
  { value: 'standard', label: 'Standard' },
  { value: 'premium', label: 'Premium' },
  { value: 'banned', label: 'Banned' },
];

const formatNumber = (n: number) => n.toLocaleString();

// Native form-control styles. No Radix portals, no clipping.
const fieldClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

export const ManageUserDialog: React.FC<ManageUserDialogProps> = ({
  user,
  open,
  onClose,
  onSave,
  onDelete,
  loading = false,
}) => {
  const [name, setName] = useState<string>(user?.name ?? '');
  const [country, setCountry] = useState<string>(user?.country ?? '');
  const [role, setRole] = useState<Role>((user?.role as Role) ?? 'reader');
  const [subscription, setSubscription] = useState<Subscription>(
    (user?.subscription as Subscription) ?? 'standard',
  );

  const [coinInput, setCoinInput] = useState<string>(
    user?.chefcoins_balance != null ? String(user.chefcoins_balance) : '0',
  );
  const [coinFocused, setCoinFocused] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Reset form when target user changes or dialog reopens.
  useEffect(() => {
    if (!open) {
      setConfirmDelete(false);
      return;
    }
    setName(user?.name ?? '');
    setCountry(user?.country ?? '');
    setRole((user?.role as Role) ?? 'reader');
    setSubscription((user?.subscription as Subscription) ?? 'standard');
    setCoinInput(
      user?.chefcoins_balance != null ? String(user.chefcoins_balance) : '0',
    );
    setError(null);
    setConfirmDelete(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, open]);

  const parsedCoins = useMemo(() => {
    if (coinInput.trim() === '') return { value: 0, error: null as string | null };
    if (!/^-?\d+$/.test(coinInput.trim())) {
      return { value: NaN, error: 'ChefCoin balance must be a whole number.' };
    }
    const n = parseInt(coinInput, 10);
    if (Number.isNaN(n)) return { value: NaN, error: 'ChefCoin balance must be a number.' };
    if (n < 0) return { value: n, error: 'ChefCoin balance must be 0 or greater.' };
    return { value: n, error: null };
  }, [coinInput]);

  const adjustCoins = (delta: number) => {
    const current = parsedCoins.value;
    const base = Number.isFinite(current) ? current : 0;
    setCoinInput(String(Math.max(0, base + delta)));
  };

  const validate = (): string | null => {
    if (!name.trim()) return 'Name is required.';
    if (name.trim().length > 64) return 'Name must be 64 characters or fewer.';
    if (country.trim()) {
      const normalized = country.trim().toUpperCase();
      if (!/^[A-Z]{2}$/.test(normalized)) {
        return 'Country must be a 2-letter code (e.g. US, CH).';
      }
      if (!COUNTRIES.some((c) => c.code === normalized)) {
        return 'Please pick a country from the list.';
      }
    }
    if (!role) return 'Role is required.';
    if (!subscription) return 'Subscription is required.';
    if (parsedCoins.error) return parsedCoins.error;
    if (Number.isNaN(parsedCoins.value)) return 'ChefCoin balance is invalid.';
    return null;
  };

  const handleSave = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    onSave({
      name: name.trim(),
      country: country.trim() ? country.trim().toUpperCase() : null,
      role,
      subscription,
      chefcoins_balance: parsedCoins.value,
    });
  };

  const handleDelete = () => {
    setConfirmDelete(false);
    onDelete();
  };

  const displayCoins = coinFocused
    ? coinInput
    : coinInput === '' || Number.isNaN(parsedCoins.value)
      ? '0'
      : formatNumber(parsedCoins.value);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        aria-describedby="manage-user-description"
        // Mobile-first: full-width on phones, capped width on larger screens.
        // No internal scroll needed on common viewports because fields stack 1-col on mobile
        // and 2-col on sm+. Country dropdown is portalled so it never gets clipped.
        className="max-w-[min(672px,calc(100vw-16px))] gap-3 p-4 sm:p-6"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-1">
          <DialogTitle>Manage User</DialogTitle>
          <DialogDescription id="manage-user-description">
            Edit user details, change role, or delete the user. All changes are immediate.
          </DialogDescription>
        </DialogHeader>

        {/* Profile summary — stacks on mobile, side-by-side on sm+ */}
        {user && (
          <div className="flex flex-col gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-sm sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{user.name || user.email}</div>
              <div className="truncate text-xs text-muted-foreground">
                {user.email}
                {user.created_at && (
                  <> &middot; Joined {new Date(user.created_at).toLocaleDateString()}</>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span><b className="text-foreground">{user.recipesCreated ?? 0}</b> recipes</span>
              <span><b className="text-foreground">{user.studioUsesCount ?? 0}</b> studio</span>
              <span><b className="text-foreground">{formatNumber(user.aiTokensUsed ?? 0)}</b> tokens</span>
              <span className="inline-flex items-center gap-1">
                <ChefCoinIcon className="h-3.5 w-3.5" />
                <b className="text-foreground">{formatNumber(user.chefcoins_balance ?? 0)}</b>
              </span>
            </div>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          // Mobile-first 1-column, 2-column on sm+ when there's room.
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          noValidate
        >
          <div className="space-y-1.5">
            <Label htmlFor="manage-user-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="manage-user-name"
              name="name"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              maxLength={64}
              autoComplete="off"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manage-user-country">
              Country <span className="text-xs font-normal text-muted-foreground">(optional)</span>
            </Label>
            <CountryDropdown
              id="manage-user-country"
              value={country}
              onChange={setCountry}
              placeholder="Select country..."
              disabled={loading}
              allowClear
            />
            {country.trim() && (
              <p className="text-xs text-muted-foreground">
                {getCountryName(country.trim().toUpperCase()) || country.trim().toUpperCase()}
                {' '}({country.trim().toUpperCase()})
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manage-user-role">
              Role <span className="text-red-500">*</span>
            </Label>
            <select
              id="manage-user-role"
              name="role"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              disabled={loading}
              className={fieldClass}
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manage-user-subscription">
              Subscription <span className="text-red-500">*</span>
            </Label>
            <select
              id="manage-user-subscription"
              name="subscription"
              value={subscription}
              onChange={(e) => setSubscription(e.target.value as Subscription)}
              disabled={loading}
              className={fieldClass}
            >
              {SUBSCRIPTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="manage-user-chefcoins" className="inline-flex items-center gap-1.5">
              <ChefCoinIcon className="h-4 w-4" />
              ChefCoin Balance
            </Label>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <Input
                id="manage-user-chefcoins"
                name="chefcoins"
                type="text"
                inputMode="numeric"
                min={0}
                value={displayCoins}
                onChange={(e) => {
                  const next = e.target.value.replace(/[^0-9]/g, '');
                  setCoinInput(next);
                }}
                onFocus={(e) => {
                  setCoinFocused(true);
                  requestAnimationFrame(() => e.target.select());
                }}
                onBlur={() => {
                  setCoinFocused(false);
                  if (coinInput.trim() === '') setCoinInput('0');
                }}
                disabled={loading}
                className="w-full sm:w-32"
                aria-invalid={!!parsedCoins.error}
              />
              <div className="flex flex-wrap items-center gap-1">
                <Button type="button" variant="outline" size="sm" onClick={() => adjustCoins(-100)} disabled={loading}>-100</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => adjustCoins(-1000)} disabled={loading}>-1k</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => adjustCoins(100)} disabled={loading}>+100</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => adjustCoins(1000)} disabled={loading}>+1k</Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setCoinInput(user?.chefcoins_balance != null ? String(user.chefcoins_balance) : '0');
                  }}
                  disabled={loading}
                >
                  Reset
                </Button>
              </div>
            </div>
            {parsedCoins.error && (
              <p className="text-xs text-red-600">{parsedCoins.error}</p>
            )}
          </div>

          {error && (
            <div className="sm:col-span-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mt-1 flex flex-col-reverse gap-2 sm:col-span-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            {!confirmDelete ? (
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-center text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30 sm:w-auto sm:justify-start"
                onClick={() => setConfirmDelete(true)}
                disabled={loading}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete user
              </Button>
            ) : (
              <div className="flex w-full flex-wrap items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="flex-1">Permanently delete this user?</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmDelete(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={loading}
                >
                  Yes, delete
                </Button>
              </div>
            )}

            <div className="flex w-full justify-end gap-2 sm:w-auto">
              <Button type="button" variant="outline" onClick={onClose} disabled={loading} className="flex-1 sm:flex-none">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !!parsedCoins.error}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white sm:flex-none"
              >
                {loading ? 'Saving...' : 'Save changes'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ManageUserDialog;
